import { normalizeMCPUrl } from "@littlebird/mcp";
import { groupBy, keyBy } from "lodash";
import { z } from "zod";
//#region src/provider-status.ts
const CONNECTING_STATUSES = [
	"pending",
	"probing",
	"connecting"
];
const ATTENTION_STATUSES = [
	"failed",
	"expired",
	"disconnected",
	"cancelled",
	"unauthorized",
	"unreachable",
	"needs_attention"
];
function aggregateProviderStatus(items) {
	const enabledItems = items.filter((item) => item.enabled);
	if (enabledItems.length === 0) return "disabled";
	if (enabledItems.some((item) => ATTENTION_STATUSES.includes(item.status))) return "needs_attention";
	if (enabledItems.some((item) => item.locality === "local" && item.status === "offline")) return "offline";
	if (enabledItems.some((item) => CONNECTING_STATUSES.includes(item.status))) return "connecting";
	if (enabledItems.every((item) => item.status === "connected")) return "usable";
	return "needs_attention";
}
//#endregion
//#region src/integration-data.ts
function getKnownData(data) {
	if (data.state === "current") return {
		value: data.value,
		stale: false,
		issues: []
	};
	return {
		value: data.lastKnown,
		stale: true,
		issues: data.state === "failed" ? [data.issue] : []
	};
}
//#endregion
//#region src/ordering.ts
function compareCodeUnits(a, b) {
	if (a === b) return 0;
	return a < b ? -1 : 1;
}
function sortByLabelThenId(items, labelOf, idOf) {
	return [...items].sort((a, b) => labelOf(a).localeCompare(labelOf(b)) || compareCodeUnits(idOf(a), idOf(b)));
}
//#endregion
//#region src/provider-key.ts
function hasUnsafeRouteCharacter(value) {
	if (value.includes("/") || value.includes("\\")) return true;
	return [...value].some((char) => {
		const code = char.charCodeAt(0);
		return code <= 31 || code === 127;
	});
}
function makeProviderKey(namespace, sourceOwnedKey) {
	if (sourceOwnedKey.length === 0) throw new Error("Provider source key must not be empty");
	if (sourceOwnedKey.includes(":")) throw new Error("Provider source key must not contain a colon");
	if (hasUnsafeRouteCharacter(sourceOwnedKey)) throw new Error("Provider source key contains an unsafe route character");
	return [namespace, sourceOwnedKey].join(":");
}
//#endregion
//#region src/build-integration-list.ts
function tryMakeProviderKey(namespace, sourceOwnedKey) {
	try {
		return makeProviderKey(namespace, sourceOwnedKey);
	} catch {
		return;
	}
}
function invalidSourceKeyIssue(source, familyLabel, offendingKey) {
	return {
		source,
		code: "invalid_source_key",
		message: `Invalid ${familyLabel} source key "${offendingKey}"`,
		retryable: false
	};
}
function toStatusContributions(items, locality) {
	return items.map((item) => ({
		enabled: item.enabled,
		locality,
		status: item.status
	}));
}
function buildComposioProviders(accountsKnown, catalogKnown) {
	const catalogByToolkit = keyBy(catalogKnown.value ?? [], (item) => item.toolkitSlug);
	const issues = [];
	return {
		providers: Object.entries(groupBy(accountsKnown.value ?? [], (account) => account.toolkitSlug)).flatMap(([toolkitSlug, rawAccounts]) => {
			const key = tryMakeProviderKey("composio", toolkitSlug);
			if (!key) {
				issues.push(invalidSourceKeyIssue("composio", "composio toolkit slug", toolkitSlug));
				return [];
			}
			const sortedRaw = sortByLabelThenId(rawAccounts, (account) => account.accountLabel, (account) => account.id);
			const catalogItem = catalogByToolkit[toolkitSlug];
			const catalogApplied = catalogItem !== void 0;
			return [{
				key,
				kind: "composio",
				availability: "everywhere",
				name: catalogItem?.displayName ?? sortedRaw[0].displayName,
				description: catalogItem?.description ?? "",
				iconUrl: catalogItem?.iconUrl ?? null,
				status: aggregateProviderStatus(toStatusContributions(rawAccounts, "remote")),
				accounts: sortedRaw.map((account) => ({
					id: account.id,
					source: "composio",
					label: account.accountLabel,
					identityHint: account.identityHint,
					endpoint: null,
					enabled: account.enabled,
					status: account.status
				})),
				localInstallations: [],
				settings: [],
				hasTools: true,
				stale: accountsKnown.stale || catalogApplied && catalogKnown.stale,
				issues: [...accountsKnown.issues, ...catalogApplied ? catalogKnown.issues : []]
			}];
		}),
		issues
	};
}
/** Normalizes every catalog endpoint once (the URL parse is the expensive
* part); first catalog entry per endpoint wins, and the index is kept so
* lookups can preserve catalog-order priority. */
function catalogByNormalizedEndpoint(catalog) {
	const byEndpoint = /* @__PURE__ */ new Map();
	catalog.forEach((item, index) => {
		const normalized = normalizeMCPUrl(item.endpoint);
		if (!byEndpoint.has(normalized)) byEndpoint.set(normalized, {
			item,
			index
		});
	});
	return byEndpoint;
}
/** The earliest catalog entry matching any of the group's endpoints — the
* same answer the previous whole-catalog find() gave, without re-parsing
* every catalog URL per provider group. */
function findMatchingCatalogItem(catalogByEndpoint, accounts) {
	let best;
	for (const account of accounts) {
		const hit = catalogByEndpoint.get(normalizeMCPUrl(account.endpoint));
		if (hit && (!best || hit.index < best.index)) best = hit;
	}
	return best?.item;
}
function buildMcpProviders(accountsKnown, catalogKnown) {
	const catalogByEndpoint = catalogByNormalizedEndpoint(catalogKnown.value ?? []);
	const issues = [];
	return {
		providers: Object.entries(groupBy(accountsKnown.value ?? [], (account) => account.slug)).flatMap(([slug, rawAccounts]) => {
			const key = tryMakeProviderKey("mcp", slug);
			if (!key) {
				issues.push(invalidSourceKeyIssue("mcp", "mcp slug", slug));
				return [];
			}
			const sortedRaw = sortByLabelThenId(rawAccounts, (account) => account.label, (account) => account.id);
			const catalogItem = findMatchingCatalogItem(catalogByEndpoint, rawAccounts);
			return [{
				key,
				kind: catalogItem !== void 0 ? "catalog_mcp" : "custom_mcp",
				availability: "everywhere",
				name: catalogItem?.name ?? sortedRaw[0].name,
				description: catalogItem?.description ?? "",
				iconUrl: catalogItem?.iconUrl ?? sortedRaw.find((account) => account.iconUrl)?.iconUrl ?? null,
				status: aggregateProviderStatus(toStatusContributions(rawAccounts, "remote")),
				accounts: sortedRaw.map((account) => ({
					id: account.id,
					source: "mcp",
					label: account.label,
					identityHint: account.identityHint,
					endpoint: account.endpoint,
					enabled: account.enabled,
					status: account.status
				})),
				localInstallations: [],
				settings: [],
				hasTools: true,
				stale: accountsKnown.stale || catalogKnown.stale,
				issues: [...accountsKnown.issues, ...catalogKnown.issues]
			}];
		}),
		issues
	};
}
function toLocalInstallation(local) {
	return {
		id: local.id,
		name: local.name,
		kind: local.kind,
		enabled: local.enabled,
		status: local.status,
		settings: local.settings
	};
}
function buildDeviceProviders(localKnown) {
	if (!localKnown) return {
		providers: [],
		issues: []
	};
	const issues = [];
	return {
		providers: Object.entries(groupBy(localKnown.value ?? [], (local) => local.toolkitSlug)).flatMap(([toolkitSlug, group]) => {
			const key = tryMakeProviderKey("device", toolkitSlug);
			if (!key) {
				issues.push(invalidSourceKeyIssue("local", "device toolkit slug", toolkitSlug));
				return [];
			}
			const sortedGroup = sortByLabelThenId(group, (local) => local.name, (local) => local.id);
			const [first] = sortedGroup;
			return [{
				key,
				kind: "device",
				availability: "this_device",
				name: first.name,
				description: first.description,
				iconUrl: first.iconUrl,
				status: aggregateProviderStatus(toStatusContributions(group, "local")),
				accounts: [],
				localInstallations: sortedGroup.map(toLocalInstallation),
				settings: sortedGroup.flatMap((local) => local.settings),
				hasTools: true,
				stale: localKnown.stale,
				issues: localKnown.issues
			}];
		}),
		issues
	};
}
function buildChannelProviders(channelsKnown) {
	const issues = [];
	return {
		providers: channelsKnown.flatMap((channelKnown) => {
			const channel = channelKnown.value;
			if (!channel) return [];
			const key = tryMakeProviderKey("channel", channel.channel);
			if (!key) {
				issues.push(invalidSourceKeyIssue("channel", "channel key", channel.channel));
				return [];
			}
			return [{
				key,
				kind: "channel",
				availability: "everywhere",
				name: channel.name,
				description: "",
				iconUrl: null,
				status: "usable",
				accounts: [],
				localInstallations: [],
				settings: [{
					kind: "channel",
					channel: channel.channel
				}],
				hasTools: false,
				stale: channelKnown.stale,
				issues: channelKnown.issues
			}];
		}),
		issues
	};
}
function sortProviders(providers) {
	return [...providers].sort((a, b) => {
		if (a.availability !== b.availability) return a.availability === "everywhere" ? -1 : 1;
		return a.name.localeCompare(b.name) || compareCodeUnits(a.key, b.key);
	});
}
function buildIntegrationList(sources) {
	const composioAccounts = getKnownData(sources.composioAccounts);
	const composioCatalog = getKnownData(sources.composioCatalog);
	const mcpAccounts = getKnownData(sources.mcpAccounts);
	const mcpCatalog = getKnownData(sources.mcpCatalog);
	const localIntegrations = sources.localIntegrations ? getKnownData(sources.localIntegrations) : void 0;
	const channelsKnown = sources.channels.map(getKnownData);
	const composio = buildComposioProviders(composioAccounts, composioCatalog);
	const mcp = buildMcpProviders(mcpAccounts, mcpCatalog);
	const channels = buildChannelProviders(channelsKnown);
	const devices = buildDeviceProviders(localIntegrations);
	return {
		integrations: sortProviders([
			...composio.providers,
			...mcp.providers,
			...channels.providers,
			...devices.providers
		]),
		issues: [
			...composioAccounts.issues,
			...composioCatalog.issues,
			...mcpAccounts.issues,
			...mcpCatalog.issues,
			...channelsKnown.flatMap((channel) => channel.issues),
			...localIntegrations?.issues ?? [],
			...composio.issues,
			...mcp.issues,
			...channels.issues,
			...devices.issues
		]
	};
}
//#endregion
//#region src/provider-tools.ts
const PROVIDER_KEY_PATTERN = /* @__PURE__ */ new RegExp("^(composio|mcp|device):[^:/\\\\\\u0000-\\u001f\\u007f]+$");
const ProviderKeySchema = z.string().regex(PROVIDER_KEY_PATTERN).transform((value) => value);
const ToolBehaviorSchema = z.enum([
	"read",
	"write",
	"delete"
]);
const ToolPermissionSchema = z.enum([
	"allow",
	"ask",
	"deny"
]);
const ProviderToolSchema = z.object({
	tool_slug: z.string(),
	display_name: z.string(),
	display_description: z.string(),
	behavior: ToolBehaviorSchema,
	sensitive: z.boolean(),
	permission: ToolPermissionSchema,
	permission_source: z.enum(["user", "default"])
}).strict();
const DiscoveryIssueSchema = z.object({
	account_id: z.string().uuid(),
	code: z.enum([
		"mcp_unreachable",
		"mcp_auth_required",
		"mcp_discovery_failed"
	]),
	retryable: z.boolean()
}).strict();
const DiscoverySchema = z.object({
	status: z.enum([
		"complete",
		"partial",
		"unavailable"
	]),
	inventory_complete: z.boolean(),
	issues: z.array(DiscoveryIssueSchema)
}).strict();
const ProviderToolsSchema = z.object({
	provider_key: ProviderKeySchema,
	discovery: DiscoverySchema,
	tools: z.array(ProviderToolSchema)
}).strict();
//#endregion
//#region src/provider-tools-api.ts
function createProviderToolsApi(sendRequest) {
	return {
		getTools: (key, options) => sendProviderToolsRequest(sendRequest, key, {
			method: "GET",
			path: providerToolsPath(key),
			signal: options?.signal
		}),
		setToolPermission: (key, toolSlug, permission, options) => sendProviderToolsRequest(sendRequest, key, {
			method: "PUT",
			path: `${providerToolsPath(key)}/tools/${encodeURIComponent(toolSlug)}/permission`,
			body: { permission },
			signal: options?.signal
		}),
		setGroupPermission: (key, behavior, permission, options) => sendProviderToolsRequest(sendRequest, key, {
			method: "PUT",
			path: `${providerToolsPath(key)}/behaviors/${encodeURIComponent(behavior)}/permission`,
			body: { permission },
			signal: options?.signal
		})
	};
}
function providerToolsPath(key) {
	return `/tools/providers/${encodeURIComponent(key)}`;
}
async function sendProviderToolsRequest(sendRequest, key, request) {
	const response = await sendRequest(request);
	const providerTools = ProviderToolsSchema.parse(response);
	if (providerTools.provider_key !== key) throw new Error("Provider tools response key mismatch");
	return providerTools;
}
//#endregion
//#region src/tool-policy.ts
const TOOL_BEHAVIOR_ORDER = [
	"read",
	"write",
	"delete"
];
const BEHAVIOR_LABELS = {
	read: "Read & search",
	write: "Create & change",
	delete: "Delete"
};
function sortByDisplayNameThenSlug(tools) {
	return sortByLabelThenId(tools, (tool) => tool.display_name, (tool) => tool.tool_slug);
}
function projectPendingPermissions(confirmed, pending) {
	const projectedBySlug = new Map(confirmed.map((confirmedTool) => [confirmedTool.tool_slug, { ...confirmedTool }]));
	for (const operation of pending) {
		if (operation.kind === "tool") {
			const projectedTool = projectedBySlug.get(operation.toolSlug);
			if (projectedTool) projectedTool.permission = operation.permission;
			continue;
		}
		for (const projectedTool of projectedBySlug.values()) if (projectedTool.behavior === operation.behavior) projectedTool.permission = operation.permission;
	}
	return sortByDisplayNameThenSlug([...projectedBySlug.values()]);
}
function groupToolsByBehavior(tools) {
	const toolsByBehavior = groupBy(tools, (tool) => tool.behavior);
	return TOOL_BEHAVIOR_ORDER.flatMap((behavior) => {
		const toolsForBehavior = toolsByBehavior[behavior] ?? [];
		if (toolsForBehavior.length === 0) return [];
		const [firstTool, ...remainingTools] = toolsForBehavior;
		const permission = remainingTools.every((tool) => tool.permission === firstTool.permission) ? firstTool.permission : "mixed";
		return [{
			behavior,
			label: BEHAVIOR_LABELS[behavior],
			permission,
			tools: sortByDisplayNameThenSlug(toolsForBehavior)
		}];
	});
}
//#endregion
export { TOOL_BEHAVIOR_ORDER, aggregateProviderStatus, buildIntegrationList, createProviderToolsApi, groupToolsByBehavior, makeProviderKey, projectPendingPermissions };

//# sourceMappingURL=index.js.map